import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MovieIcon from "@mui/icons-material/Movie";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

// Favorite movies data
const favoriteMovies = [
  {
    title: "Interstellar",
    image: "https://image.tmdb.org/t/p/w500/gEU2QniE6E77NI6lCU6MxlNBvIx.jpg",
    year: 2014,
    director: "Christopher Nolan",
  },
  {
    title: "Dune",
    image:
      "https://a.ltrbxd.com/resized/sm/upload/nx/8b/vs/gc/cDbNAY0KM84cxXhmj8f0dLWza3t-0-2000-0-3000-crop.jpg?v=49eed12751",
    year: 2021,
    director: "Denis Villeneuve",
  },
  {
    title: "The Matrix",
    image:
      "https://a.ltrbxd.com/resized/film-poster/5/1/5/1/8/51518-the-matrix-0-2000-0-3000-crop.jpg?v=fc7c366afe",
    year: 1999,
    director: "Bong Joon-ho",
  },
  {
    title: "Django Unchained",
    image:
      "https://a.ltrbxd.com/resized/film-poster/5/2/5/1/6/52516-django-unchained-0-2000-0-3000-crop.jpg?v=f02aed63a3",
    year: 2012,
    director: "Quentin Tarantino",
  },
];

// Photography styles
const photographyStyles = [
  {
    title: "Favourite Football Team",
    description: "Real Madrid",
    icon: <SportsSoccerIcon />,
  },
  {
    title: "Music Genres",
    description: "All kinds, from vintage to house music, and beyond.",
    icon: <LibraryMusicIcon />,
  },
];

// Simple styled components
const StyledCard = styled(Card)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-8px)",
  },
});

const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`interests-tabpanel-${index}`}
      aria-labelledby={`interests-tab-${index}`}
      sx={{ padding: 3 }}
    >
      {value === index && children}
    </Box>
  );
};

function Interests({ parentToChild }: { parentToChild?: { mode: string } }) {
  const mode = parentToChild?.mode || "dark";
  const [value, setValue] = useState(0);

  return (
    <Box
      id="interests"
      className="items-container"
      sx={{
        padding: { xs: "2rem 1rem", md: "4rem 2rem" },
        backgroundColor: mode === "dark" ? "#0a0d12" : "#f8f9fa",
        borderRadius: "16px",
        margin: { xs: "1rem", md: "2rem" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h2"
        component="h2"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: mode === "dark" ? "white" : "#0d1116",
          width: "100%",
          fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
        }}
      >
        More About Me
      </Typography>

      <Tabs
        value={value}
        onChange={(_, newValue) => setValue(newValue)}
        aria-label="interests tabs"
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#5000ca" } }}
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            color:
              mode === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            "&.Mui-selected": { color: mode === "dark" ? "white" : "#0d1116" },
          },
        }}
        centered
      >
        <Tab icon={<MovieIcon />} label="Movies" />
        <Tab icon={<ViewModuleIcon />} label="Other" />
      </Tabs>

      <TabPanel value={value} index={0}>
        <Box
          sx={{ mb: 3, textAlign: "center", width: "100%", maxWidth: "800px" }}
        >
          <Typography
            variant="body1"
            component="p"
            sx={{
              mb: 2,
              color:
                mode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            }}
          >
            I love watching movies, those are some of my favourite movies but if
            you want to see more you can check my{" "}
            <Typography
              component="a"
              href="https://boxd.it/bXCg5"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: mode === "dark" ? "white" : "#5000ca",
                textDecoration: "none",
                fontWeight: "bold",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Letterboxd
            </Typography>{" "}
            Account.
          </Typography>
        </Box>

        <Box sx={{ width: "100%", maxWidth: "900px", mx: "auto" }}>
          <Grid
            container
            spacing={{ xs: 2, md: 4 }}
            sx={{
              justifyContent: "center",
            }}
          >
            {favoriteMovies.map((movie, index) => (
              <Grid
                item
                xs={6}
                sm={6}
                md={3}
                key={index}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <StyledCard
                  sx={{
                    backgroundColor: mode === "dark" ? "#1a1d23" : "white",
                    color: mode === "dark" ? "white" : "inherit",
                    maxWidth: { md: "200px" },
                    width: "100%",
                  }}
                >
                  <CardMedia
                    component="img"
                    height="180"
                    image={movie.image}
                    alt={movie.title}
                  />
                  <CardContent>
                    <Typography
                      gutterBottom
                      variant="subtitle2"
                      component="div"
                      align="center"
                    >
                      {movie.title} ({movie.year})
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <Box
          sx={{ mb: 3, textAlign: "center", width: "100%", maxWidth: "800px" }}
        >
          <Typography
            variant="body1"
            component="p"
            sx={{
              mb: 2,
              color:
                mode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            }}
          >
            I built this portfolio recently, so I'll keep sharing more soon. ;)
          </Typography>
        </Box>

        <Box sx={{ width: "100%", maxWidth: "800px", mx: "auto" }}>
          <Paper
            elevation={mode === "dark" ? 2 : 1}
            sx={{
              borderRadius: "12px",
              backgroundColor: mode === "dark" ? "#1a1d23" : "white",
              overflow: "hidden",
            }}
          >
            <List>
              {photographyStyles.map((style, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom:
                      index < photographyStyles.length - 1
                        ? `1px solid ${mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`
                        : "none",
                    py: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{ color: mode === "dark" ? "white" : "#5000ca" }}
                  >
                    {style.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: mode === "dark" ? "white" : "inherit",
                          fontWeight: 600,
                        }}
                      >
                        {style.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            mode === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.6)",
                        }}
                      >
                        {style.description}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      </TabPanel>
    </Box>
  );
}

export default Interests;
